import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Phone, Mail, MapPin, Users, Search, CheckCircle2, PhoneCall, ClipboardCheck,
  ShieldAlert, ExternalLink, Lock, FileText, ArrowRight, ArrowDown, UserCheck, ChevronRight,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";

const WORKFLOW = [
  { n: 1, title: "IDENTIFY", desc: "Inspection or service needed", icon: Search },
  { n: 2, title: "CONTACT", desc: "Contact the appropriate state warehouse, account manager or Sales agent", icon: PhoneCall },
  { n: 3, title: "CONFIRM", desc: "Confirm availability, capability, required parts and approval", icon: CheckCircle2 },
  { n: 4, title: "ESCALATE", desc: "Use Leather Doctor when required and approved", icon: ShieldAlert },
];

const MUST_PROVIDE = [
  "MWC / warranty case reference",
  "Customer suburb and postcode",
  "Product name / model",
  "Description of issue",
  "Photos / videos or other evidence",
  "Troubleshooting already completed",
  "Proposed inspection / repair scope",
  "Required parts if known",
  "Whether a charge is expected",
];

const BOOKING = {
  "Before Booking": [
    "Confirm which function or side is affected",
    "Confirm product model and labels",
    "Confirm customer-reported symptoms",
    "Record troubleshooting already completed",
    "Collect photos / videos where possible",
    "If troubleshooting cannot be completed, record why",
  ],
  "Parts": [
    "Identify the likely required part",
    "Ask technical support if diagnosis is unclear",
    "Check stock / donor part / ETA where required",
    "Record proposed visit scope and cost",
    "Do not send random parts if the diagnosis is unclear",
  ],
  "Contact": [
    "Email the relevant state contact directly and confirm: availability",
    "Capability",
    "Required parts",
    "Expected cost",
    "Proposed visit scope",
  ],
  "Before Visit": [
    "Confirm parts have arrived",
    "Technician / agent has access to required parts",
    "Appointment date / time",
    "Local timezone",
    "Customer access",
    "Scope of visit",
    "Where possible, arrange inspection and repair during the same attendance",
  ],
};

const REPORT = [
  "Warranty / case reference", "Visit date", "Product / model", "Customer-reported symptoms",
  "Checks performed", "Observations", "Photos", "Findings", "Parts fitted", "Work completed",
  "Function tests performed", "Test results", "Any unresolved concerns", "Further parts required",
  "Further visit required", "Recommended next action",
];

const CLOSING = [
  "Obtain technician / agent report", "Record repair outcome", "Update the customer",
  "Update the retailer where applicable", "Update the Warranty Portal", "Update the live Warranty Tracker",
];

function Check({ children }) {
  return (
    <li className="flex gap-2 text-sm text-slate-700">
      <span className="w-4 h-4 rounded border border-slate-300 shrink-0 mt-0.5" />{children}
    </li>
  );
}

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  useEffect(() => { api.get("/content/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);

  const active = contacts.filter((c) => (c.active || "Active") !== "Inactive");

  return (
    <div className="space-y-8" data-testid="contacts-page">
      <PageHeader overline="Directory" title="Inspection & Service Contacts" icon={Phone}
        desc="Arrange inspections and repairs through the correct state contact. No confidential passwords are stored here." />

      {/* 2. Contact workflow */}
      <Card className="p-6" data-testid="contact-workflow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {WORKFLOW.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="relative flex md:flex-col items-start md:items-stretch">
                <div className={`flex-1 rounded-xl border-2 p-4 ${s.n === 4 ? "border-red-300 bg-red-50" : "border-mlily-blue/30 bg-blue-50"}`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${s.n === 4 ? "text-red-600" : "text-mlily-blue"}`} />
                    <span className={`font-heading font-extrabold ${s.n === 4 ? "text-red-700" : "text-mlily-blue"}`}>{s.n}. {s.title}</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">{s.desc}</p>
                </div>
                {i < WORKFLOW.length - 1 && (
                  <>
                    <ChevronRight className="hidden md:block w-5 h-5 text-slate-300 absolute -right-4 top-1/2 -translate-y-1/2" />
                    <ArrowDown className="md:hidden w-5 h-5 text-slate-300 mx-4 self-center" />
                  </>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-sm text-slate-500 mt-4 flex items-center gap-2">
          <FileText className="w-4 h-4" /> Record the booking, report and outcome in the portal and live Warranty Tracker.
        </p>
      </Card>

      {/* 1. State directory */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-mlily-blue" />
          <h2 className="font-heading text-xl font-bold text-slate-900">State Inspection & Service Contacts</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {active.map((c) => (
            <Card key={c.id} className="p-5" data-testid={`state-contact-${c.id}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="font-heading font-bold text-slate-900 text-lg">{c.state}</h3>
                {c.service_area && <Badge variant="outline">{c.service_area}</Badge>}
              </div>
              {/* Primary */}
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700">
                  <UserCheck className="w-3.5 h-3.5" /> Arrange First
                </div>
                <div className="mt-1 font-semibold text-slate-900">{c.primary_name}</div>
                <div className="text-xs text-slate-500">{c.primary_role}</div>
                {c.primary_email && (
                  <a href={`mailto:${c.primary_email}`} className="flex items-center gap-2 text-sm text-mlily-blue mt-1 hover:underline">
                    <Mail className="w-4 h-4" />{c.primary_email}
                  </a>
                )}
                {c.phone && (
                  <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-sm text-slate-600 mt-1"><Phone className="w-4 h-4" />{c.phone}</a>
                )}
              </div>
              {/* Alternative */}
              {c.alt_name ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mt-3">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Alternative Contact</div>
                  <div className="mt-1 font-semibold text-slate-900">{c.alt_name}</div>
                  <div className="text-xs text-slate-500">{c.alt_role}</div>
                  {c.alt_email && (
                    <a href={`mailto:${c.alt_email}`} className="flex items-center gap-2 text-sm text-mlily-blue mt-1 hover:underline">
                      <Mail className="w-4 h-4" />{c.alt_email}
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-400 mt-3">No alternative contact currently listed.</p>
              )}
              {c.notes && c.notes !== "No alternative contact currently listed." && (
                <p className="text-xs text-slate-500 mt-3">{c.notes}</p>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* 3. Information staff must provide */}
      <Card className="p-6" data-testid="must-provide">
        <div className="flex items-center gap-2 mb-4 font-heading font-bold text-slate-900">
          <ClipboardCheck className="w-5 h-5 text-mlily-blue" /> Information Staff Must Provide
        </div>
        <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
          {MUST_PROVIDE.map((t, i) => <Check key={i}>{t}</Check>)}
        </ul>
        <div className="mt-4 flex items-start gap-2 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          Do not confirm the visit to the customer until availability, capability, approval and parts timing are confirmed.
        </div>
      </Card>

      {/* 4. Sales / Agent inspection rule */}
      <Card className="p-6" data-testid="sales-agent-rule">
        <div className="flex items-center gap-2 mb-3 font-heading font-bold text-slate-900">
          <Users className="w-5 h-5 text-mlily-blue" /> Sales / Agent Inspection Rule
        </div>
        <ul className="space-y-2 text-sm text-slate-700 list-disc pl-5">
          <li>An Agent or Sales representative may attend where the customer cannot safely complete troubleshooting, including customers who require additional assistance.</li>
          <li>Sales representatives should only perform tasks within their training.</li>
          <li>Technical repairs must be completed by a competent agent, technician, MLILY warehouse team member or other approved person.</li>
        </ul>
      </Card>

      {/* 5. Leather Doctor escalation */}
      <Card className="p-6 border-2 border-amber-300 bg-amber-50/50" data-testid="leather-doctor-section">
        <div className="flex items-center gap-2 mb-3 text-amber-700 font-heading font-bold text-lg">
          <ShieldAlert className="w-5 h-5" /> Leather Doctor Escalation
        </div>
        <p className="text-sm text-slate-700">Use Leather Doctor when:</p>
        <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-5">
          <li>MLILY warehouse cannot attend</li>
          <li>Account Manager cannot attend</li>
          <li>Relevant Sales agent cannot attend</li>
          <li>An approved external technician is required</li>
        </ul>
        <div className="mt-3 flex items-start gap-2 rounded-lg border-2 border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /> Approval must be obtained before arranging Leather Doctor.
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <a href="https://system1.myleatherdoctor.com.au/NEW/" target="_blank" rel="noopener noreferrer"
            data-testid="leather-doctor-portal-link"
            className="flex items-center justify-between gap-2 rounded-lg bg-mlily-blue text-white px-4 py-3 hover:bg-mlily-bluehover transition-colors">
            <span className="text-sm font-semibold">Open Leather Doctor Booking Portal</span>
            <ExternalLink className="w-4 h-4" />
          </a>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
            <div className="text-slate-500">Username</div>
            <div className="font-mono font-semibold text-slate-900">NickH</div>
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">
          <Lock className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
          <span><span className="font-semibold">Password:</span> Obtain from MLILY secure password records. The password is never displayed or stored in this portal.</span>
        </div>
      </Card>

      {/* 6. Arranging an inspection / repair */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <ClipboardCheck className="w-5 h-5 text-mlily-blue" />
          <h2 className="font-heading text-xl font-bold text-slate-900">Arranging an Inspection / Repair</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="arrange-checklist">
          {Object.entries(BOOKING).map(([group, items]) => (
            <Card key={group} className="p-5">
              <h4 className="font-heading font-bold text-slate-900 mb-3">{group}</h4>
              <ul className="space-y-2">{items.map((t, i) => <Check key={i}>{t}</Check>)}</ul>
            </Card>
          ))}
        </div>
      </div>

      {/* 7. Important inspection rule */}
      <Card className="p-6 border-2 border-red-500 bg-red-50" data-testid="inspection-rule-warning">
        <div className="flex items-center gap-2 text-red-700 font-heading font-extrabold text-lg">
          <ShieldAlert className="w-6 h-6" /> AN INSPECTION IS NOT AUTOMATICALLY A COMPLETED REPAIR
        </div>
        <ul className="mt-3 space-y-1.5 text-sm text-red-800 list-disc pl-5">
          <li>If diagnosis is unclear, arrange a diagnostic visit rather than sending random replacement parts.</li>
          <li>Do not delay an urgent safety inspection solely because parts are unavailable.</li>
          <li>If a repair must occur later, record the separate repair plan.</li>
        </ul>
      </Card>

      {/* 8. Required report */}
      <Card className="p-6" data-testid="required-report">
        <div className="flex items-center gap-2 mb-4 font-heading font-bold text-slate-900">
          <FileText className="w-5 h-5 text-mlily-blue" /> Required Inspection / Agent Report
        </div>
        <p className="text-sm text-slate-500 mb-3">After attendance, staff must obtain a report containing:</p>
        <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
          {REPORT.map((t, i) => (
            <li key={i} className="flex gap-2 text-sm text-slate-700"><ArrowRight className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />{t}</li>
          ))}
        </ul>
        <div className="mt-4 flex items-start gap-2 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          Do not accept a report containing only wording such as “All good.” The report must explain what was checked and the findings.
        </div>
      </Card>

      {/* 9. Closing the case */}
      <Card className="p-6" data-testid="closing-case">
        <div className="flex items-center gap-2 mb-4 font-heading font-bold text-slate-900">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Closing the Case
        </div>
        <ul className="space-y-2">{CLOSING.map((t, i) => <Check key={i}>{t}</Check>)}</ul>
        <div className="mt-4 flex items-start gap-2 rounded-lg border-2 border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          If the technician / agent report conflicts with the customer's experience, record both versions and escalate for review. Do not automatically close the warranty case.
        </div>
      </Card>
    </div>
  );
}
